<template>
  <Fragment>
    <el-form-item :label="label || '省/市'" prop="cityCode">
      <span v-if="disabled">{{ valueName }}</span>
      <QS-cascader
        v-else
        v-model="internalValue"
        filterable
        clearable
        showAllLevels
        :placeholder="placeholder"
        :options="provinceList"
        :filterMethod="filterMethod"
        :disabled="disabled"
        @change="provinceChange">
      </QS-cascader>
    </el-form-item>
  </Fragment>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { queryAllProvinceAndCity } from '@/api/global'

// 定义 props
const props = defineProps(['provinceCode', 'cityCode', 'provinceCityModel', 'disabled', 'label', 'provinceCityName', 'placeholder', 'modelValue'])

// 定义 emits
const emit = defineEmits(['change', 'update:modelValue'])

// 响应式数据
const selectProvince = ref(props.provinceCode)
const selectCity = ref(props.cityCode)
const provinceList = ref([]) // 品牌列表
const internalValue = ref([])
const allProvinceList = ref([])

// 计算属性
const valueName = computed(() => {
  const { provinceCityName } = props
  const { provinceName, cityName } = provinceCityName || {}
  if (!provinceName) return '--'
  if (!cityName) return provinceName
  return `${provinceName}/${cityName}`
})

// 方法
const handleProvinceCodeClear = () => {
  selectCity.value = null
}

const reset = () => {
  selectProvince.value = null
  selectCity.value = null
  internalValue.value = []
  emit('update:modelValue', null)
  emit('change', { provinceCode: null, cityCode: null })
}

const provinceChange = (newVal) => {
  if (!newVal || newVal.length === 0) {
    provinceList.value = allProvinceList.value
    emit('update:modelValue', null)
  }
  const provinceCode = newVal?.[0] || null
  const cityCode = newVal?.[1] || null
  emit('update:modelValue', { provinceCode, cityCode })
  emit('change', { provinceCode, cityCode })
}

const filterMethod = (node, keyword) => {
  if (!keyword) return true

  const lowerKeyword = keyword.toLowerCase()
  const nodeText = node.text.toLowerCase()
  return nodeText.includes(lowerKeyword)
}

// 获取省
const getAllProvinceAndCity = async () => {
  const { code, data } = await queryAllProvinceAndCity()
  if (code !== 1) return
  // 设置省份可选项
  if (code === 1) {
    const list = data?.map((item) => {
      const children = item.cities?.map((city) => ({
        label: city.cityName,
        value: city.cityCode,
        leaf: true,
      })) || []
      return {
        label: item.provinceName,
        value: item.provinceCode,
        children,
      }
    }) || []
    provinceList.value = list || []
    allProvinceList.value = list || []
  }
}

// 监听器
watch(() => props.provinceCode, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    selectProvince.value = newVal
  }
  handleProvinceCodeClear()
})

watch(() => props.cityCode, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    selectCity.value = newVal
  }
})

watch(() => props.provinceCityModel, (newVal, oldVal) => {
  const { provinceCode, cityCode } = newVal || {}
  if (cityCode) {
    internalValue.value = [provinceCode, cityCode]
  } else {
    internalValue.value = [provinceCode]
  }
  const { provinceCode: oldProvinceCode, cityCode: oldCityCode } = oldVal || {}
  if (provinceCode !== oldProvinceCode) {
    selectProvince.value = provinceCode || null
    handleProvinceCodeClear()
  }
  if (cityCode !== oldCityCode) {
    selectCity.value = cityCode || null
  }
}, { deep: true })

// 监听 v-model 值的变化
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    const { provinceCode, cityCode } = newVal
    if (cityCode) {
      internalValue.value = [provinceCode, cityCode]
    } else {
      internalValue.value = [provinceCode]
    }
  } else {
    internalValue.value = []
  }
})

onMounted(() => {
  getAllProvinceAndCity()
})

// 暴露需要在模板中使用的变量和方法
defineExpose({
  reset,
})
</script>
<script>
export default {
  name: 'ProvinceCityList',
}
</script>
